import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  Link,
  useParams,
} from "react-router-dom";
import { profileSlice } from "redux/features/profile";
import {
  Message,
  Container,
  Form,
  Columns,
  Content,
} from "react-bulma-components";
import { dualSort, caseInsensitive, ascending } from "util/sort";
import ActivityDates from "./dates";
import IconButton from "util/iconButton";
import { FaEdit, FaArrowLeft } from "react-icons/fa";
import MathContent from "util/mathjax";
import dayjs from "dayjs";

const { Field, Label, Control, Select } = Form;

function ShowParticipation({ participation }) {
  const { activity, attending, isInProgram } = participation;
  // Don't report the "on waiting list" status explicitly for a past event;
  // just leave it as the catch-all "Did not attend" below.
  const otherRoles = ["organizer", "speaker", "cancelled", "declined"].filter(
    (r) => participation.roles[r]
  );
  const navigate = useNavigate();
  // Subprograms: indent 6 Bulma units (pl=6, for paddding-left).
  const colAttr = isInProgram ? { pl: 6 } : {};
  // Use less padding between rows: No padding-bottom, and padding-top only
  // between main program rows. Note that numbers like 3 and 6 can be specified
  // either as numbers or strings but zero needs to be specified as a string.
  colAttr.pt = isInProgram ? "0" : 3;
  colAttr.pb = "0";
  return (
    <Fragment>
      <Columns.Column size="one-quarter" {...colAttr}>
        <ActivityDates activity={activity} full />
      </Columns.Column>
      <Columns.Column size="half" {...colAttr}>
        <MathContent>{participation.activity_title}</MathContent>
      </Columns.Column>
      <Columns.Column size="one-quarter" {...colAttr}>
        {otherRoles.length > 0 ? (
          <div className="roles">
            Status: {otherRoles.join(", ")}.
            {attending === null ? (
              <span className="unconfirmed-attendee">
                {" "}
                Not registered
                {
                  // Add once we actually have past-confirmation enabled:
                  false ? "; please confirm attendance." : "."
                }
              </span>
            ) : attending ? null : (
              <span className="not-attending"> Did not attend.</span>
            )}
          </div>
        ) : null}
        {
          // Re-enable this once we actually are showing some useful
          // registration details for old, non-portal-registration events:
          null && (
            <div className="buttons">
              <IconButton
                icon={FaEdit}
                onClick={(e) => navigate("../../participation/" + activity.id)}
              >
                Details
              </IconButton>
            </div>
          )
        }
      </Columns.Column>
    </Fragment>
  );
}

export function PastParticipationsByYear() {
  const { data } = useSelector(profileSlice.selector);
  const { year } = useParams();

  const thisYear = dayjs().year();
  const maxYear = dayjs().month() > 5 ? thisYear + 1 : thisYear;

  const allParticipations = data.participations
    .map((p) => {
      // Bring "activity" properties up to the top level for sorting
      const { start_date, end_date, title: activity_title } = p.activity;
      return { start_date, end_date, activity_title, ...p };
    })
    .filter((p) => p.show && p.is_past)
    .sort(
      dualSort(
        "start_date",
        caseInsensitive,
        ascending,
        "activity_title",
        caseInsensitive,
        ascending
      )
    );

  const firstParticipation = allParticipations
    .map((p) => p.start_date)
    .sort()[0];
  const firstYear = firstParticipation
    ? dayjs(firstParticipation).month() > 5
      ? 1 + dayjs(firstParticipation).year()
      : dayjs(firstParticipation).year()
    : thisYear;
  const minYear = firstYear > maxYear ? maxYear : firstYear;

  const yearOptions = [];
  let y;
  for (y = minYear - 1; y <= maxYear; y++) {
    yearOptions.push({
      value: y,
      label:
        y < minYear
          ? "prior to July " + y
          : "July " + (y - 1) + " through June " + y,
    });
  }
  const yearOptionLabels = Object.fromEntries(
    yearOptions.map(({ value, label }) => [value, label])
  );

  const navigate = useNavigate();

  const numericYear = Number(year);

  if (!yearOptionLabels[year])
    return <Navigate to={String(thisYear)} replace />;

  const filterMin = String(numericYear - 1) + "-07-01";
  const filterMax = year + "-06-30";
  const participations = allParticipations
    .filter((p) => p.end_date >= filterMin && p.start_date <= filterMax)
    .map((p) => {
      return { subParticipations: [], isInProgram: false, ...p };
    });

  const participationIndex = Object.fromEntries(
    participations.map((p) => [p.activity.id, p])
  );

  participations.forEach((p) =>
    p.activity.part_of_program.forEach((programId) => {
      const programParticipation = participationIndex[programId];
      if (programParticipation) {
        programParticipation.subParticipations.push(p);
        p.isInProgram = true;
      }
    })
  );

  return (
    <Container className="spaced">
      <Field>
        <Label>View Past Participations by Year:</Label>
        <Control>
          <Select
            value={year}
            onChange={(e) => {
              navigate("../" + e.target.value);
            }}
          >
            {yearOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Control>
      </Field>
      <Message>
        <Message.Header>
          Past Activity Registrations: {yearOptionLabels[year]}
        </Message.Header>
        <Message.Body>
          <Message color="warning">
            <Message.Body>
              <Content>
                {participations.length === 0 ? (
                  <p>
                    <b>No registrations found in this time period.</b>
                  </p>
                ) : null}
                <p>
                  If you attended an activity during this period
                  {participations.length > 0 ? (
                    <Fragment>
                      {" "}
                      that is{" "}
                      <b>
                        <i>not listed</i>
                      </b>{" "}
                      here
                    </Fragment>
                  ) : null}
                  , the registration may be under a different profile. Please
                  use the following instructions to merge your profiles:{" "}
                </p>
                <ol>
                  <li>
                    Go to the{" "}
                    <Link to="../../basic/emails">
                      e-mail address editing section
                    </Link>{" "}
                    and add <i>all e-mail addresses</i> you have used when
                    registering in the past.
                  </li>
                  <li>
                    You'll be told when an address belongs to a different
                    profile. You'll have the option of making it "shared with
                    another person" or "merging the profiles"; choose the merge
                    option.
                  </li>
                </ol>
              </Content>
            </Message.Body>
          </Message>
          <Columns>
            {participations
              .filter((p) => !p.isInProgram)
              .map((p) => (
                <Fragment key={p.activity.id}>
                  <ShowParticipation participation={p} />
                  {p.subParticipations.map((subp) => (
                    <ShowParticipation
                      key={subp.activity.id}
                      participation={subp}
                    />
                  ))}
                </Fragment>
              ))}
          </Columns>
          <div className="buttons">
            <IconButton icon={FaArrowLeft} onClick={() => navigate("../..")}>
              Go Back
            </IconButton>
          </div>
        </Message.Body>
      </Message>
    </Container>
  );
}

export function PastParticipationsSection() {
  const thisYear = dayjs().year();
  return (
    <Routes>
      <Route path=":year" element={<PastParticipationsByYear />}></Route>
      <Route path="*" element={<Navigate to={String(thisYear)} replace />} />
    </Routes>
  );
}

export default PastParticipationsSection;
